import React, { useRef, useState } from 'react';
import { Plus, Trash, X } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabaseClient';
import {
  GALLERY_CATEGORIES,
  galleryCategoryLabel,
  deleteGalleryPhotoFromUrl,
  resolveGalleryPhotoUrl,
  uploadGalleryPhoto,
} from '@/lib/galleryPhotoStorage';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

const MAX_FILE_BYTES = 5 * 1024 * 1024;
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

const GalleryTab = ({ photos, onRefresh }) => {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState({
    category: 'fleet',
    caption: '',
    sort_order: '0',
  });
  const [pendingFiles, setPendingFiles] = useState([]);

  const handleFileSelect = (e) => {
    const selected = Array.from(e.target.files || []);
    if (!selected.length) return;

    const valid = [];
    const rejected = [];

    selected.forEach((file) => {
      if (!ALLOWED_TYPES.includes(file.type)) {
        rejected.push(`${file.name}: unsupported type`);
        return;
      }
      if (file.size > MAX_FILE_BYTES) {
        rejected.push(`${file.name}: over 5 MB`);
        return;
      }
      valid.push({ file, previewUrl: URL.createObjectURL(file) });
    });

    if (rejected.length) {
      toast.error(rejected.length === 1 ? rejected[0] : `${rejected.length} files skipped (type or size)`);
    }
    if (!valid.length) return;

    setPendingFiles((prev) => {
      prev.forEach((item) => URL.revokeObjectURL(item.previewUrl));
      return valid;
    });
  };

  const removePendingFile = (previewUrl) => {
    setPendingFiles((prev) => {
      const item = prev.find((p) => p.previewUrl === previewUrl);
      if (item) URL.revokeObjectURL(item.previewUrl);
      const next = prev.filter((p) => p.previewUrl !== previewUrl);
      if (!next.length && fileRef.current) fileRef.current.value = '';
      return next;
    });
  };

  const clearPendingFiles = () => {
    setPendingFiles((prev) => {
      prev.forEach((item) => URL.revokeObjectURL(item.previewUrl));
      return [];
    });
    if (fileRef.current) fileRef.current.value = '';
  };

  const resetForm = () => {
    setForm({ category: 'fleet', caption: '', sort_order: '0' });
    setPendingFiles((prev) => {
      prev.forEach((item) => URL.revokeObjectURL(item.previewUrl));
      return [];
    });
    if (fileRef.current) fileRef.current.value = '';
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!pendingFiles.length) {
      toast.error('Choose one or more image files');
      return;
    }

    setUploading(true);
    const baseSort = Number(form.sort_order) || 0;
    const caption = form.caption.trim() || null;
    let uploaded = 0;

    try {
      for (let i = 0; i < pendingFiles.length; i += 1) {
        const { file } = pendingFiles[i];
        const imageUrl = await uploadGalleryPhoto(file, form.category);
        const now = new Date().toISOString();
        const { error } = await supabase.from('gallery_photos').insert({
          id: crypto.randomUUID(),
          image_url: imageUrl,
          category: form.category,
          caption,
          sort_order: baseSort + i,
          is_active: true,
          created_at: now,
          updated_at: now,
        });
        if (error) {
          try {
            await deleteGalleryPhotoFromUrl(imageUrl);
          } catch {
            /* best effort rollback */
          }
          throw error;
        }
        uploaded += 1;
      }

      toast.success(
        uploaded === 1 ? 'Photo uploaded' : `${uploaded} photos uploaded`,
      );
      resetForm();
      onRefresh();
    } catch (err) {
      if (uploaded > 0) {
        onRefresh();
        toast.error(
          `${uploaded} uploaded, then failed: ${err.message || 'upload error'}`,
        );
      } else {
        toast.error(err.message || 'Failed to upload photos');
      }
    } finally {
      setUploading(false);
    }
  };

  const toggleActive = async (photo) => {
    try {
      const { error } = await supabase
        .from('gallery_photos')
        .update({ is_active: !photo.is_active, updated_at: new Date().toISOString() })
        .eq('id', photo.id);
      if (error) throw error;
      onRefresh();
    } catch {
      toast.error('Failed to update photo');
    }
  };

  const handleDelete = async (photo) => {
    if (!window.confirm('Delete this gallery photo? This cannot be undone.')) return;
    try {
      try {
        await deleteGalleryPhotoFromUrl(photo.image_url);
      } catch {
        /* legacy or missing storage object */
      }
      const { error } = await supabase.from('gallery_photos').delete().eq('id', photo.id);
      if (error) throw error;
      toast.success('Photo deleted');
      onRefresh();
    } catch (err) {
      toast.error(err.message || 'Failed to delete photo');
    }
  };

  return (
    <div className="space-y-8">
      <Card className="border-atithi-border">
        <CardHeader>
          <CardTitle className="font-display">Upload Gallery Photos</CardTitle>
          <p className="text-sm text-[#888] font-body mt-1">
            Select one or more images. Uploads go to the gallery-photos bucket. Categories are fixed.
          </p>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleUpload} className="space-y-4 max-w-xl">
            <div>
              <Label htmlFor="gallery-file">Image files</Label>
              <Input
                id="gallery-file"
                ref={fileRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                multiple
                onChange={handleFileSelect}
                className="mt-1"
              />
              {pendingFiles.length > 0 && (
                <div className="mt-3">
                  <div className="mb-2 flex items-center justify-between gap-2">
                    <p className="text-xs text-gray-500 font-body">
                      {pendingFiles.length === 1
                        ? '1 photo selected'
                        : `${pendingFiles.length} photos selected — sort order increments from the value below.`}
                    </p>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={clearPendingFiles}
                      className="h-7 shrink-0 px-2 text-xs text-gray-600 hover:text-red-600"
                    >
                      Clear all
                    </Button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {pendingFiles.map((item) => (
                      <div key={item.previewUrl} className="relative">
                        <img
                          src={item.previewUrl}
                          alt={item.file.name}
                          title={item.file.name}
                          className="h-20 w-20 rounded-lg border border-gray-200 object-cover"
                        />
                        <button
                          type="button"
                          onClick={() => removePendingFile(item.previewUrl)}
                          className="absolute -right-1.5 -top-1.5 flex h-5 w-5 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-600 shadow-sm hover:bg-red-50 hover:text-red-600"
                          aria-label={`Remove ${item.file.name}`}
                        >
                          <X size={12} weight="bold" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div>
              <Label htmlFor="gallery-category">Category</Label>
              <select
                id="gallery-category"
                value={form.category}
                onChange={(e) => setForm((p) => ({ ...p, category: e.target.value }))}
                className="mt-1 flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm font-body"
              >
                {GALLERY_CATEGORIES.map((c) => (
                  <option key={c.value} value={c.value}>
                    {c.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <Label htmlFor="gallery-caption">
                Caption (optional{pendingFiles.length > 1 ? ', applied to all selected' : ''})
              </Label>
              <Input
                id="gallery-caption"
                value={form.caption}
                onChange={(e) => setForm((p) => ({ ...p, caption: e.target.value }))}
                placeholder="Short description shown on hover"
                className="mt-1"
              />
            </div>

            <div>
              <Label htmlFor="gallery-sort">Sort order</Label>
              <Input
                id="gallery-sort"
                type="number"
                min="0"
                value={form.sort_order}
                onChange={(e) => setForm((p) => ({ ...p, sort_order: e.target.value }))}
                className="mt-1 w-32"
              />
            </div>

            <Button
              type="submit"
              disabled={uploading || pendingFiles.length === 0}
              className="bg-[#1B2D5E] hover:bg-[#E8651A] text-white"
            >
              <Plus size={18} className="mr-2" />
              {uploading
                ? 'Uploading…'
                : pendingFiles.length > 1
                  ? `Add ${pendingFiles.length} photos`
                  : 'Add photo'}
            </Button>
          </form>
        </CardContent>
      </Card>

      <Card className="border-atithi-border">
        <CardHeader>
          <CardTitle className="font-display">Gallery Photos ({photos.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {photos.length === 0 ? (
            <p className="text-center text-sm text-[#888] py-10 font-body">No photos yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Preview</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead>Caption</TableHead>
                    <TableHead>Order</TableHead>
                    <TableHead>Active</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {photos.map((photo) => (
                    <TableRow key={photo.id}>
                      <TableCell>
                        <img
                          src={resolveGalleryPhotoUrl(photo.image_url)}
                          alt={photo.caption || galleryCategoryLabel(photo.category)}
                          className="h-14 w-20 rounded object-cover border border-gray-200"
                        />
                      </TableCell>
                      <TableCell className="font-medium">{galleryCategoryLabel(photo.category)}</TableCell>
                      <TableCell className="max-w-[200px] truncate text-sm text-gray-600">
                        {photo.caption || '—'}
                      </TableCell>
                      <TableCell>{photo.sort_order ?? 0}</TableCell>
                      <TableCell>
                        <Switch
                          checked={photo.is_active !== false}
                          onCheckedChange={() => toggleActive(photo)}
                          aria-label="Toggle active"
                        />
                      </TableCell>
                      <TableCell>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          onClick={() => handleDelete(photo)}
                        >
                          <Trash size={16} className="mr-1" />
                          Delete
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default GalleryTab;
